/**
 * DispatchEditDialog — Print - Dispatch editable shipping-document list.
 * Access port: `frmCtrlSalesDispatchPrint` (subform) + `btnPrint_Click`.
 *   - Show HẾT danh sách document (1 dòng / option public_shipping_doc). Cột
 *     Document là TEXT cố định (không sửa); user chỉ nhập Original(s)/Copies.
 *     Prefill Original(s)/Copies từ data ĐÃ LƯU (cp_vn_shippingdoc theo cp_code,
 *     query CtrlSalesQryDispatchPrint_LstDoc). Bấm Print chỉ lưu dòng có Original/Copies.
 *   - "Print" → POST /dispatch-print/save (soft-delete + upsert cp_vn_shippingdoc)
 *     rồi render rpCtrlSalesDispatch → trả HTML cho parent mở DispatchPrintDialog.
 */
import { type CSSProperties, useEffect, useState } from 'react'
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogContent,
  DialogTitle, Stack, Table, TableBody, TableCell, TableHead,
  TableRow, TextField, Typography,
} from '@mui/material'
import { type SxProps, type Theme } from '@mui/material/styles'

import {
  type DispatchDocRow,
  useLazyGetDispatchPrintFormQuery,
  useSaveDispatchPrintMutation,
} from '../../store/api/saleControlApi'
import { useSnackbar } from '../../hooks/useSnackbar'
import { getErrorMessage } from '../../utils/error'

// Match form-dialog standard (DeliveryOrderFormDialog): title button + field font.
const titleBtnSx: SxProps<Theme> = { minWidth: 0, px: 1.25, py: 0.3, fontSize: '0.78rem', textTransform: 'none' }
const fieldSx: SxProps<Theme> = {
  '& .MuiInputBase-root': { minHeight: 28, fontSize: '0.8125rem' },
  '& .MuiInputBase-input': { py: 0.25, fontSize: '0.8125rem' },
}
// Compact rows — tighten vertical padding so the long document list stays scannable.
const cellSx: SxProps<Theme> = { fontSize: '0.8125rem', py: 0.25 }
const numCellSx: SxProps<Theme> = { py: 0.25 }
const headCellSx: SxProps<Theme> = { fontWeight: 900, fontSize: '0.8125rem', py: 0.5 }
// Sticky header — TLC cream→gold gradient (skill /table-style `app-table-header-cell`).
// Inline style (specificity 1,0,0,0) beats MUI `.MuiTableCell-stickyHeader` z-index/bg,
// and the OPAQUE gradient stops scrolled rows from bleeding through.
const stickyHeaderStyle: CSSProperties = {
  position: 'sticky',
  top: 0,
  zIndex: 5,
  backgroundColor: '#fff9f4',
  backgroundImage: 'linear-gradient(180deg, #fff9f4 0%, #f4df9c 100%)',
  color: '#0b3633',
}

interface Props {
  open: boolean
  doNo: string | null
  canEdit: boolean
  onClose: () => void
  /** Sau khi lưu thành công → mở preview với HTML render từ BE. */
  onPrinted: (title: string, html: string) => void
}

type DraftRow = { docName: string; numOriginal: string; numCopies: string }

const EMPTY_ROW: DraftRow = { docName: '', numOriginal: '', numCopies: '' }

const toNum = (v: string): number | null => {
  const s = v.trim()
  if (!s) return null
  const n = Number(s)
  return Number.isFinite(n) ? n : null
}

const hasQty = (r: DraftRow) => r.numOriginal.trim() !== '' || r.numCopies.trim() !== ''

// Alphabetical by document name, but rows that have Original(s)/Copies float to the top.
const sortDraftRows = (arr: DraftRow[]): DraftRow[] =>
  [...arr].sort((a, b) => {
    const qa = hasQty(a) ? 0 : 1
    const qb = hasQty(b) ? 0 : 1
    return qa !== qb ? qa - qb : a.docName.localeCompare(b.docName)
  })

export default function DispatchEditDialog({ open, doNo, canEdit, onClose, onPrinted }: Props) {
  const snack = useSnackbar()
  const [loadForm, { isFetching }] = useLazyGetDispatchPrintFormQuery()
  const [saveDispatch, { isLoading: saving }] = useSaveDispatchPrintMutation()
  const [rows, setRows] = useState<DraftRow[]>([])
  const [loadError, setLoadError] = useState(false)

  useEffect(() => {
    if (!open || !doNo) return
    let cancelled = false
    setLoadError(false)
    void (async () => {
      try {
        const data = await loadForm(doNo).unwrap()
        if (cancelled) return
        const opts = data.docOptions ?? []
        // Show the FULL document list (one row per shipping_doc option, fixed text).
        // Pre-fill Original(s)/Copies from the documents PREVIOUSLY SAVED for this
        // contract (cp_vn_shippingdoc by cp_code — Access CtrlSalesQryDispatchPrint_LstDoc),
        // matched by document name. On Print, only rows with a quantity are saved.
        const saved = new Map<string, DispatchDocRow>(
          (data.rows ?? [])
            .filter((r): r is DispatchDocRow & { docName: string } => !!r.docName)
            .map((r) => [r.docName, r]),
        )
        const toDraft = (name: string, r?: DispatchDocRow): DraftRow => ({
          docName: name,
          numOriginal: r?.numOriginal != null ? String(r.numOriginal) : '',
          numCopies: r?.numCopies != null ? String(r.numCopies) : '',
        })
        const seeded: DraftRow[] = opts.map((name) => toDraft(name, saved.get(name)))
        // Keep any saved docs not in the current options list (legacy / inactive).
        for (const r of saved.values()) {
          if (!opts.includes(r.docName as string)) seeded.push(toDraft(r.docName as string, r))
        }
        setRows(seeded.length ? sortDraftRows(seeded) : [{ ...EMPTY_ROW }])
      } catch {
        if (!cancelled) setLoadError(true)
      }
    })()
    return () => { cancelled = true }
  }, [open, doNo, loadForm])

  // Re-sort after each edit so a row that gains Original(s)/Copies floats to the top
  // immediately. Stable row key (docName) keeps input focus while the row moves.
  const setRow = (idx: number, patch: Partial<DraftRow>) =>
    setRows((p) => sortDraftRows(p.map((r, i) => (i === idx ? { ...r, ...patch } : r))))

  const handlePrint = async () => {
    if (!canEdit || !doNo) return
    // Access btnPrint_Click saves a row when its document combo is selected.
    // Since the full document list is now shown, only persist rows where the
    // user entered a quantity — Original(s) <> '' OR Copies <> ''.
    const payloadRows: DispatchDocRow[] = rows
      .filter((r) => r.docName.trim() && (r.numOriginal.trim() !== '' || r.numCopies.trim() !== ''))
      .map((r) => ({ docName: r.docName.trim(), numOriginal: toNum(r.numOriginal), numCopies: toNum(r.numCopies) }))
    try {
      const res = await saveDispatch({ doNo, rows: payloadRows }).unwrap()
      onPrinted(res.title, res.html)
      onClose()
    } catch (err) {
      snack.error(getErrorMessage(err, 'Save dispatch documents failed'))
    }
  }

  return (
    <Dialog open={open} maxWidth="md" fullWidth onClose={(_e, reason) => { if (saving || reason === 'backdropClick') return; onClose() }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}>
        <Typography component="span" variant="subtitle1" sx={{ fontWeight: 700 }}>Print - Dispatch{doNo ? ` (${doNo})` : ''}</Typography>
        <Stack direction="row" spacing={0.75} sx={{ ml: 'auto' }}>
          <Button
            type="button"
            variant="contained"
            color="success"
            sx={titleBtnSx}
            disabled={!canEdit || saving || isFetching || loadError}
            onClick={() => void handlePrint()}
          >
            {saving ? <CircularProgress size={14} color="inherit" /> : 'Print'}
          </Button>
          <Button type="button" variant="outlined" sx={titleBtnSx} disabled={saving} onClick={onClose}>Cancel</Button>
        </Stack>
      </DialogTitle>
      {/* pt:0 — sticky header pins flush to the top with no padding band above it
          (otherwise scrolled rows peek through the gap over the column-name row). */}
      <DialogContent dividers sx={{ pt: 0 }}>
        {!canEdit && <Alert severity="info" sx={{ mt: 1.5, mb: 1.5 }}>You do not have permission to edit dispatch documents.</Alert>}
        {loadError ? (
          <Alert severity="error" sx={{ mt: 1.5 }}>Could not load dispatch documents. Please try again.</Alert>
        ) : isFetching ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, py: 8 }}>
            <CircularProgress size={24} />
            <Typography variant="body2" color="text.secondary">Loading…</Typography>
          </Box>
        ) : (
          <>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={headCellSx} style={stickyHeaderStyle}>Document</TableCell>
                  <TableCell sx={{ ...headCellSx, width: 130 }} style={stickyHeaderStyle} align="right">Original(s)</TableCell>
                  <TableCell sx={{ ...headCellSx, width: 130 }} style={stickyHeaderStyle} align="right">Copies</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row, idx) => (
                  <TableRow key={row.docName || idx}>
                    <TableCell sx={cellSx}>{row.docName}</TableCell>
                    <TableCell align="right" sx={numCellSx}>
                      <TextField size="small" fullWidth value={row.numOriginal} sx={fieldSx}
                        onChange={(e) => setRow(idx, { numOriginal: e.target.value })}
                        inputProps={{ inputMode: 'numeric', style: { textAlign: 'right' } }} />
                    </TableCell>
                    <TableCell align="right" sx={numCellSx}>
                      <TextField size="small" fullWidth value={row.numCopies} sx={fieldSx}
                        onChange={(e) => setRow(idx, { numCopies: e.target.value })}
                        inputProps={{ inputMode: 'numeric', style: { textAlign: 'right' } }} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
